/**
 * Wishlist Service
 *
 * Handles all wishlist-related API calls
 */

import api from './api';

export const wishlistService = {
  /**
   * Get wishlist
   * @returns {Promise<Object>} Wishlist with items
   */
  getWishlist: async () => {
    try {
      const response = await api.get('/wishlist');

      // Backend returns array directly in data
      // Transform to {items: array, count: number} format
      if (response.data.success && Array.isArray(response.data.data)) {
        return {
          success: true,
          data: {
            items: response.data.data,
            count: response.data.data.length
          }
        };
      }

      return response.data;
    } catch (error) {
      console.error('[WishlistService] Get wishlist error:', error);
      throw error;
    }
  },

  /**
   * Add item to wishlist
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Updated wishlist
   */
  addToWishlist: async (productId) => {
    try {
      // Add to wishlist (returns single item)
      const addResponse = await api.post('/wishlist', { productId });

      if (!addResponse.data.success) {
        return addResponse.data;
      }

      // Fetch updated full wishlist
      const wishlistResponse = await api.get('/wishlist');

      // Transform to expected format
      if (wishlistResponse.data.success && Array.isArray(wishlistResponse.data.data)) {
        return {
          success: true,
          message: addResponse.data.message,
          data: {
            items: wishlistResponse.data.data,
            count: wishlistResponse.data.data.length
          }
        };
      }

      return wishlistResponse.data;
    } catch (error) {
      console.error('[WishlistService] Add to wishlist error:', error);
      throw error;
    }
  },

  /**
   * Remove item from wishlist
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Updated wishlist
   */
  removeFromWishlist: async (productId) => {
    try {
      // Remove from wishlist (returns null data)
      const deleteResponse = await api.delete(`/wishlist/${productId}`);

      if (!deleteResponse.data.success) {
        return deleteResponse.data;
      }

      // Fetch updated full wishlist
      const wishlistResponse = await api.get('/wishlist');

      // Transform to expected format
      if (wishlistResponse.data.success && Array.isArray(wishlistResponse.data.data)) {
        return {
          success: true,
          message: deleteResponse.data.message,
          data: {
            items: wishlistResponse.data.data,
            count: wishlistResponse.data.data.length
          }
        };
      }

      return wishlistResponse.data;
    } catch (error) {
      console.error('[WishlistService] Remove from wishlist error:', error);
      throw error;
    }
  },

  /**
   * Check if product is in wishlist
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>} True if product is in wishlist
   */
  isInWishlist: async (productId) => {
    try {
      // Use dedicated check endpoint (more efficient)
      const response = await api.get(`/wishlist/check/${productId}`);

      if (response.data.success && typeof response.data.data === 'boolean') {
        return response.data.data;
      }

      return false;
    } catch (error) {
      console.error('[WishlistService] Check wishlist error:', error);
      return false;
    }
  },

  /**
   * Toggle product in wishlist (add if not present, remove if present)
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Updated wishlist
   */
  toggleWishlist: async (productId) => {
    try {
      const isIn = await wishlistService.isInWishlist(productId);

      if (isIn) {
        return await wishlistService.removeFromWishlist(productId);
      } else {
        return await wishlistService.addToWishlist(productId);
      }
    } catch (error) {
      console.error('[WishlistService] Toggle wishlist error:', error);
      throw error;
    }
  },

  /**
   * Get wishlist count
   * @returns {Promise<number>} Number of items in wishlist
   */
  getWishlistCount: async () => {
    try {
      // Use dedicated count endpoint (more efficient)
      const response = await api.get('/wishlist/count');

      if (response.data.success && typeof response.data.data === 'number') {
        return response.data.data;
      }

      return 0;
    } catch (error) {
      console.error('[WishlistService] Get wishlist count error:', error);
      return 0;
    }
  }
};

export default wishlistService;
